import { useMemo } from 'react'
import { useAuth } from '../../Contexts/AuthContext'
import useAdminAccess from './useAdminAccess'
import { isPermissionAllowed } from '../helpers/getUserPermissions'
import { calculateWriteAccess } from '../helpers/permissionCalculator'

/**
 * Check if user has read-only access to content.
 *
 * @param {string|string[]} permissions - Permission string(s) to check (e.g., 'music_song:r')
 * @param {Object} [item] - Optional item object with organizations array for org-aware checking
 * @returns {{ isReadOnly: boolean }} - Returns true if user can only read (not edit)
 *
 * When item is provided:
 * - Checks user's role in the item's organizations/groups
 * - Returns read-only if user doesn't have write access in ANY of item's orgs
 * - Handles multi-org users correctly
 *
 * When item is NOT provided:
 * - Falls back to general permission checking (old behavior)
 * - Useful for list/search pages where full item data isn't available
 */
export default function useReadOnly(permissions, item = null) {
  const { currentAuthUser, getCurrentUser } = useAuth()
  const { isInternal } = useAdminAccess()
  const user = currentAuthUser || getCurrentUser()

  const isReadOnly = useMemo(() => {
    // If no item provided, use old behavior (general permission check)
    if (!item) {
      const permissionsArray = Array.isArray(permissions)
        ? permissions
        : [permissions]

      return permissionsArray.some(permission =>
        isPermissionAllowed({
          user,
          permission,
        })
      )
    }

    // Item provided - do org-aware checking using pure function
    const hasWriteAccess = calculateWriteAccess(user, item, isInternal)
    return !hasWriteAccess
  }, [user, currentAuthUser, isInternal, permissions, item])

  return { isReadOnly }
}
